/**
 * components/procurement/ProjectedPropertyDialog.jsx — add a hypothetical
 * "new project" property by cloning an existing one and scaling its meters.
 *
 * Helps users see what they could spend over the horizon for new projects /
 * acquisitions alongside their existing portfolio. Cloned scopes are tagged.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Building2 } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { addProjectedProperty } from '../../api/budgets';

const ALL = '__all__';
const money = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

export function ProjectedPropertyDialog({ open, onOpenChange, accountId, budgetId, sources = [], onSaved }) {
  const [sourceKey, setSourceKey] = useState('');
  const [name, setName] = useState('');
  const [multiplier, setMultiplier] = useState('2');
  const [commodity, setCommodity] = useState(ALL);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setSourceKey(''); setName(''); setMultiplier('2'); setCommodity(ALL); }
  }, [open]);

  const source = useMemo(
    () => sources.find((s) => s.property_key === sourceKey),
    [sources, sourceKey],
  );

  const mult = parseFloat(multiplier) || 0;
  const projectedAnnual = source ? source.annual_total * mult : 0;

  const submit = async () => {
    if (!sourceKey) { toast.error('Pick a property to clone'); return; }
    if (!name.trim()) { toast.error('Name the new project'); return; }
    if (mult <= 0) { toast.error('Meter multiplier must be greater than 0'); return; }
    setSaving(true);
    try {
      await addProjectedProperty({
        accountId, id: budgetId,
        payload: {
          name: name.trim(),
          source_property_key: sourceKey,
          meter_multiplier: mult,
          commodities: commodity === ALL ? null : [commodity],
          tag: 'new-project',
        },
      });
      toast.success('New project property added', { description: `${name.trim()} — projected from ${source?.property_name}` });
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error('Could not add projected property', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="projected-property-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-600" /> Add a new-project property
          </DialogTitle>
          <DialogDescription>
            Clone an existing property's usage &amp; rate profile to model a hypothetical
            new project. Scale it by a meter multiplier — it flows into the projection
            (tagged as a new project) but is excluded from budget-vs-actual.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Clone from property</Label>
            <Select value={sourceKey} onValueChange={setSourceKey}>
              <SelectTrigger data-testid="projected-source-select"><SelectValue placeholder="Pick an existing property" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {sources.map((s) => (
                  <SelectItem key={s.property_key} value={s.property_key}>
                    {s.property_name} · {money(s.annual_total)}/yr · {s.meter_count} meter{s.meter_count === 1 ? '' : 's'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="proj-name">New project name</Label>
            <Input id="proj-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Riverside Phase II" data-testid="projected-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="proj-mult">Meter multiplier</Label>
              <Input id="proj-mult" type="number" min="0.1" step="0.1" value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)} data-testid="projected-multiplier-input" />
              <p className="text-[11px] text-muted-foreground">2 = twice the meters/usage of the source.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Commodities</Label>
              <Select value={commodity} onValueChange={setCommodity}>
                <SelectTrigger data-testid="projected-commodity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All from source</SelectItem>
                  {(source?.commodities || []).map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {source && (
            <div className="border rounded-md p-3 bg-emerald-50/50 text-sm" data-testid="projected-preview">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Projected annual baseline</span>
                <span className="font-semibold text-emerald-700">{money(projectedAnnual)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {source.property_name} ({money(source.annual_total)}/yr) × {mult || 0}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="projected-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Adding…</> : 'Add new project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
